/**
 * Product analytics on Usami (self-hosted Umami).
 *
 * Event names follow `<area>_<object>_<action in past tense>` in snake_case,
 * e.g. `research_link_copied`. Umami caps names at 50 characters and event
 * data values must be strings, numbers or booleans. Never send PII of the
 * interesado (names, emails, phones) nor bank data (CLABE): send ids
 * (`broker_id`, `research_id`, `payout_id`), counts and states instead.
 * Every event carries `broker_id` (see setAnalyticsContext); Umami stamps the date.
 *
 * The full catalog with the metric each event validates lives in
 * analytics/investigaciones.md and casas-y-terrenos/cytpanel#634. Keep them in sync.
 */

const DEFAULT_HOST = "https://usami.bekind.software";

export const ANALYTICS_EVENTS = {
  // Home (/inicio) entry point to Investigaciones
  HOME_RESEARCH_CTA_CLICKED: "home_research_cta_clicked",

  // Investigaciones general view (/investigaciones)
  RESEARCH_LIST_LOADED: "research_list_loaded",
  RESEARCH_LINK_COPIED: "research_link_copied",
  RESEARCH_LINK_COPY_FAILED: "research_link_copy_failed",
  RESEARCH_COMMISSIONS_CTA_CLICKED: "research_commissions_cta_clicked",
  RESEARCH_REPORT_PDF_OPENED: "research_report_pdf_opened",
  RESEARCH_LIST_FILTERED: "research_list_filtered",
  RESEARCH_LIST_PAGE_CHANGED: "research_list_page_changed",

  // Commissions (/investigaciones/comisiones)
  COMMISSION_BANK_ACCOUNT_SAVED: "commission_bank_account_saved",
  COMMISSION_BANK_ACCOUNT_SAVE_FAILED: "commission_bank_account_save_failed",
  COMMISSION_PAYOUT_REQUESTED: "commission_payout_requested",
  COMMISSION_PAYOUT_REQUEST_FAILED: "commission_payout_request_failed",
  COMMISSION_PAYOUT_HISTORY_OPENED: "commission_payout_history_opened",

  // Payout detail (/investigaciones/comisiones/:id)
  COMMISSION_TAX_DATA_COPIED: "commission_tax_data_copied",
  COMMISSION_INVOICE_UPLOADED: "commission_invoice_uploaded",
  COMMISSION_INVOICE_UPLOAD_FAILED: "commission_invoice_upload_failed",
};

// Events fired while the tracker script is loading are flushed once it loads.
// Stays null when analytics is disabled so nothing piles up.
let pendingEvents = null;
const MAX_PENDING_EVENTS = 50;

// Properties sent with every event, e.g. the broker the session belongs to
let context = {};

/**
 * Merge properties into every following event. Pass `undefined` to drop one.
 * @param {Object<string, string|number|boolean|undefined>} properties
 */
export function setAnalyticsContext(properties) {
  context = { ...context, ...properties };
  Object.keys(context).forEach((key) => context[key] === undefined && delete context[key]);
}

function send(event, data) {
  try {
    window.umami.track(event, data);
  } catch (error) {
    $log.error("Analytics event could not be sent", { event, error });
  }
}

/**
 * Track a product event on Usami. Safe to call when analytics is disabled.
 * @param {string} event - One of ANALYTICS_EVENTS
 * @param {Object<string, string|number|boolean>} [data]
 */
export function track(event, data) {
  if (typeof window === "undefined") return;
  const payload = { ...context, ...data };
  if (window.umami?.track) {
    send(event, payload);
  } else if (pendingEvents && pendingEvents.length < MAX_PENDING_EVENTS) {
    pendingEvents.push([event, payload]);
  }
}

function flushPendingEvents() {
  const events = pendingEvents || [];
  pendingEvents = null;
  events.forEach(([event, data]) => send(event, data));
}

function createUsamiScript(env, file) {
  const script = document.createElement("script");
  script.defer = true;
  script.src = `${env.VITE_UMAMI_HOST || DEFAULT_HOST}/${file}`;
  script.dataset.websiteId = env.VITE_UMAMI_WEBSITE_ID;
  return script;
}

/**
 * Inject the Usami tracker and the session recorder. Does nothing unless
 * VITE_UMAMI_WEBSITE_ID is set, so local builds don't pollute production data.
 *
 * The recorder (rrweb) only records once the tracker has a session, samples the
 * share of sessions set in Usami and masks inputs; visible text with personal
 * data must carry the `rr-mask` class. VITE_UMAMI_RECORDER=false turns it off.
 */
export function initAnalytics(env = import.meta.env) {
  const websiteId = env.VITE_UMAMI_WEBSITE_ID;
  if (!websiteId || document.querySelector("script[data-website-id]")) return;

  const script = createUsamiScript(env, "script.js");
  if (env.VITE_UMAMI_DOMAINS) script.dataset.domains = env.VITE_UMAMI_DOMAINS;
  script.onload = flushPendingEvents;
  // Blocked by an ad blocker or down: drop what was queued
  script.onerror = () => {
    pendingEvents = null;
  };
  pendingEvents = [];
  document.head.appendChild(script);

  if (env.VITE_UMAMI_RECORDER !== "false") {
    document.head.appendChild(createUsamiScript(env, "recorder.js"));
  }
}
